<script setup>
import { computed } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { formatShortDate } from '@/lib/utils'

import { exchangeRateSourceEnum } from '@/stores/portal.store'

import { useCurrency } from '@/composables/currency'
import {
  currencyCode,
  ecbExchangeRate,
  formatExchangeRate,
  isReferenceCurrency,
  manualExchangeRate,
  manualTableExchangeRate,
  parseExchangeRate,
} from '@/composables/exchange-rate'

import InfoBox from '@/components/shared/InfoBox.vue'
import { useReadonlyField } from '@/composables/readonly-form'

const EXAMPLE_AMOUNT = 100

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  currencyRef: {
    type: Number,
    default: null,
  },
  noRateHint: {
    type: String,
    default: null,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
})

const isReadonly = useReadonlyField(() => props.readonly)

const emit = defineEmits(['update:modelValue'])

const i18n = getI18n()
const { getCurrencyString } = useCurrency()

const ecbRate = computed(() => ecbExchangeRate(props.currencyRef))
const isManual = computed(() => !ecbRate.value || props.modelValue.exchange_rate_source === exchangeRateSourceEnum.MANUAL)
const foreignCode = computed(() => currencyCode(props.currencyRef))
const baseCode = computed(() => currencyCode(props.modelValue.exchange_rate_base_currency_ref))
const rateLabel = computed(() => `1 ${baseCode.value} =`)

const SUMMARY_KEYS = Object.freeze({
  [exchangeRateSourceEnum.MANUAL]: 'company.exchangeRates.manualRateSummary',
  [exchangeRateSourceEnum.ECB]: 'company.exchangeRates.ecbRateSummary',
  [exchangeRateSourceEnum.ECB_DERIVED]: 'company.exchangeRates.ecbDerivedRateSummary',
})
const summaryKey = computed(() => SUMMARY_KEYS[props.modelValue.exchange_rate_source])
const exampleReferenceAmount = computed(() => EXAMPLE_AMOUNT / Number(props.modelValue.exchange_rate))

const onSourceToggle = (useEcb) => emit('update:modelValue', useEcb
  ? ecbRate.value
  : manualTableExchangeRate(props.currencyRef) ?? manualExchangeRate(props.modelValue.exchange_rate, props.modelValue))

const onRateChange = (value) => emit('update:modelValue', manualExchangeRate(parseExchangeRate(value), props.modelValue))

const onDateChange = (date) => emit('update:modelValue', { ...props.modelValue, exchange_rate_date: date })
</script>
<template>
  <div v-if="!isReferenceCurrency(props.currencyRef)">
    <div class="row q-col-gutter-sm items-center">
      <q-input
        :model-value="props.modelValue.exchange_rate"
        class="col-12 col-sm-4"
        type="number"
        min="0"
        step="any"
        input-class="text-right"
        :label="rateLabel"
        :suffix="foreignCode"
        :readonly="isReadonly || !isManual"
        @change="onRateChange"
      />
      <q-input
        :model-value="props.modelValue.exchange_rate_date"
        class="col-12 col-sm-4"
        type="date"
        stack-label
        :label="i18n.t('company.exchangeRates.rateDate')"
        :readonly="isReadonly || !isManual"
        @update:model-value="onDateChange"
      />
      <q-toggle
        :model-value="!isManual"
        class="col-12 col-sm-4"
        :label="isManual ? i18n.t('company.exchangeRates.useManualRate') : i18n.t('company.exchangeRates.useEcbRate')"
        :disable="isReadonly || !ecbRate"
        color="primary"
        @update:model-value="onSourceToggle"
      />
    </div>
    <info-box class="q-mt-sm" :icon="isManual ? 'mdi-pencil-outline' : 'mdi-bank-outline'">
      <template v-if="props.modelValue.exchange_rate == null">
        {{ props.noRateHint ?? i18n.t('company.exchangeRates.enterRate') }}
      </template>
      <template v-else>
        <i18n-t :keypath="summaryKey" tag="div" scope="global">
          <template #date><b>{{ formatShortDate(props.modelValue.exchange_rate_date) }}</b></template>
          <template #rate><b>{{ rateLabel }} {{ formatExchangeRate(props.modelValue.exchange_rate) }} {{ foreignCode }}</b></template>
        </i18n-t>
        <i18n-t
          keypath="company.exchangeRates.exchangeRateExample"
          tag="div"
          scope="global"
          class="q-mt-xs"
        >
          <template #foreign><b>{{ getCurrencyString(EXAMPLE_AMOUNT, foreignCode) }}</b></template>
          <template #reference><b>{{ getCurrencyString(exampleReferenceAmount, baseCode) }}</b></template>
        </i18n-t>
      </template>
    </info-box>
  </div>
</template>
